import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, Users, ShoppingBag, Eye } from 'lucide-react';

const earningsData = [
  { name: 'Mon', earnings: 45000, trades: 8 },
  { name: 'Tue', earnings: 52000, trades: 12 },
  { name: 'Wed', earnings: 38000, trades: 6 },
  { name: 'Thu', earnings: 61000, trades: 15 },
  { name: 'Fri', earnings: 75000, trades: 18 },
  { name: 'Sat', earnings: 82000, trades: 22 },
  { name: 'Sun', earnings: 68000, trades: 16 },
];

const monthlyData = [
  { name: 'Jan', earnings: 320000, trades: 95 },
  { name: 'Feb', earnings: 380000, trades: 110 },
  { name: 'Mar', earnings: 420000, trades: 125 },
  { name: 'Apr', earnings: 390000, trades: 115 },
  { name: 'May', earnings: 450000, trades: 135 },
  { name: 'Jun', earnings: 520000, trades: 158 },
];

const currencyData = [
  { name: 'USD', value: 45, color: '#22c55e' },
  { name: 'EUR', value: 25, color: '#3b82f6' },
  { name: 'GBP', value: 15, color: '#8b5cf6' },
  { name: 'NGN', value: 10, color: '#f59e0b' },
  { name: 'Others', value: 5, color: '#6b7280' },
];

const stats = [
  { title: 'Total Earnings', value: '₦2.4M', change: '+18.2%', trend: 'up', icon: DollarSign },
  { title: 'Total Trades', value: '1,247', change: '+12.5%', trend: 'up', icon: ShoppingBag },
  { title: 'Total Views', value: '45.2K', change: '+25.3%', trend: 'up', icon: Eye },
  { title: 'Unique Customers', value: '892', change: '+8.7%', trend: 'up', icon: Users },
];

export default function VendorAnalytics() {
  return (
    
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Analytics</h1>
          <p className="text-muted-foreground">Track your business performance</p>
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.title}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{stat.title}</p>
                      <p className="text-2xl font-bold">{stat.value}</p>
                      <p className={`text-sm ${stat.trend === 'up' ? 'text-emerald-500' : 'text-red-500'}`}>
                        {stat.change}
                      </p>
                    </div>
                    <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-emerald-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Tabs defaultValue="weekly" className="space-y-4">
          <TabsList>
            <TabsTrigger value="weekly">Weekly</TabsTrigger>
            <TabsTrigger value="monthly">Monthly</TabsTrigger>
            <TabsTrigger value="currency">By Currency</TabsTrigger>
          </TabsList>

          <TabsContent value="weekly">
            <Card>
              <CardHeader>
                <CardTitle>Weekly Performance</CardTitle>
                <CardDescription>Earnings and trades for the past week</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={earningsData}>
                    <defs>
                      <linearGradient id="colorEarnings" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                    <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `₦${v/1000}k`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                    />
                    <Area type="monotone" dataKey="earnings" stroke="#10b981" fillOpacity={1} fill="url(#colorEarnings)" strokeWidth={2} />
                    <Line type="monotone" dataKey="trades" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="monthly">
            <Card>
              <CardHeader>
                <CardTitle>Monthly Performance</CardTitle>
                <CardDescription>Earnings and trades by month</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                    <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `₦${v/1000}k`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                    />
                    <Bar dataKey="earnings" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="currency">
            <Card>
              <CardHeader>
                <CardTitle>Trading by Currency</CardTitle>
                <CardDescription>Distribution of trades by currency</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6 lg:grid-cols-2">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={currencyData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {currencyData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                        formatter={(value: number) => [`${value}%`, '']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-4">
                    {currencyData.map((curr) => (
                      <div key={curr.name} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="h-4 w-4 rounded-full" style={{ backgroundColor: curr.color }} />
                          <span className="font-medium">{curr.name}</span>
                        </div>
                        <span className="text-muted-foreground">{curr.value}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    
  );
}
